import React, { useEffect } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx } from "../../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { usePeriods } from "../../hooks/usePeriods";
import { PERIOD_NAME_MAX } from "./periodValidation";
import { UnsavedChangesBar } from "../../../settings/components/UnsavedChangesBar";
import { useNotify } from "../../../../context/NotificationContext";

const headCellSx = { fontWeight: 600, py: 1, bgcolor: "grey.50" };
const fieldSx = { "& .MuiInputBase-root": { height: 34, fontSize: 14 }, "& .MuiFormHelperText-root": { mx: 0 } };

/**
 * The school's one bell schedule: periods and breaks with their times. Edited in place and saved
 * together; rows are renumbered by start time on save. Each grade then picks its periods per day
 * (Grade Periods tab).
 */
const BellScheduleUI: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const notify = useNotify();
  const { rows, errors, changedKeys, dirty, loading, saving, error, actions } = usePeriods();

  // warn before closing/reloading the tab with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const handleSave = async () => {
    if (await actions.save()) notify("Bell schedule saved.", "success");
  };

  const lessonCount = rows.filter((row) => !row.isBreak).length;

  return (
    <Box>
      <PageTopBar
        left={tabs ?? "Bell Schedule"}
        right={
          <Button variant="contained" size="small" sx={toolbarButtonSx} startIcon={<AddIcon />} onClick={actions.add} disabled={loading || saving}>
            Add Period
          </Button>
        }
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        One schedule for the whole school. Mark lunch and short breaks as <b>Break</b>; each grade picks its periods per day in{" "}
        <b>Grade Periods</b>.
      </Typography>

      {error && !dirty && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 48 }}>#</TableCell>
              <TableCell sx={headCellSx}>Name</TableCell>
              <TableCell sx={{ ...headCellSx, width: 150 }}>Start</TableCell>
              <TableCell sx={{ ...headCellSx, width: 150 }}>End</TableCell>
              <TableCell sx={{ ...headCellSx, width: 80 }} align="center">
                Break
              </TableCell>
              <TableCell sx={{ ...headCellSx, width: 56 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography fontWeight={600}>No periods yet</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add the school's periods and breaks with their timings.
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={actions.add}>
                    Add the first period
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const rowErrors = errors[index] ?? {};
                const changed = changedKeys.includes(row.key);
                return (
                  <TableRow
                    key={row.key}
                    sx={{
                      bgcolor: (theme) =>
                        changed ? alpha(theme.palette.warning.light, 0.12) : row.isBreak ? "grey.50" : undefined,
                      "&:last-child td": { border: 0 },
                      "& td": { verticalAlign: "top", py: 1 },
                    }}
                  >
                    <TableCell sx={{ pt: "15px !important", color: "text.secondary" }}>{index + 1}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.name}
                        onChange={(event) => actions.update(row.key, { name: event.target.value })}
                        error={Boolean(rowErrors.name)}
                        helperText={rowErrors.name}
                        disabled={saving}
                        slotProps={{ htmlInput: { maxLength: PERIOD_NAME_MAX, "aria-label": `Period ${index + 1} name` } }}
                        sx={{ ...fieldSx, maxWidth: 260, "& input": { fontStyle: row.isBreak ? "italic" : undefined } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="time"
                        fullWidth
                        value={row.startTime}
                        onChange={(event) => actions.update(row.key, { startTime: event.target.value })}
                        error={Boolean(rowErrors.startTime)}
                        helperText={rowErrors.startTime}
                        disabled={saving}
                        slotProps={{ htmlInput: { "aria-label": `Period ${index + 1} start` } }}
                        sx={fieldSx}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="time"
                        fullWidth
                        value={row.endTime}
                        onChange={(event) => actions.update(row.key, { endTime: event.target.value })}
                        error={Boolean(rowErrors.endTime)}
                        helperText={rowErrors.endTime}
                        disabled={saving}
                        slotProps={{ htmlInput: { "aria-label": `Period ${index + 1} end` } }}
                        sx={fieldSx}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        size="small"
                        checked={row.isBreak}
                        onChange={(event) => actions.update(row.key, { isBreak: event.target.checked })}
                        disabled={saving}
                        inputProps={{ "aria-label": `Period ${index + 1} is a break` }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove">
                        <span>
                          <IconButton size="small" onClick={() => actions.remove(row.key)} disabled={saving} aria-label={`Remove ${row.name}`}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && rows.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {lessonCount} teaching period{lessonCount === 1 ? "" : "s"}, {rows.length - lessonCount} break
          {rows.length - lessonCount === 1 ? "" : "s"}. Rows are put in time order when saved.
        </Typography>
      )}

      {/* last child of the page box, so it sticks to the bottom of the screen while scrolling */}
      <Box sx={{ height: 16 }} />
      <UnsavedChangesBar open={dirty} saving={saving} error={error} onDiscard={actions.discard} onSave={handleSave} />
    </Box>
  );
};

export default BellScheduleUI;
